import { useCallback, useEffect, useMemo, useState } from "react";
import { TextField, Grid, useTheme, FormControl, InputLabel, Select, MenuItem, FormControlLabel, FormLabel, Switch } from "@mui/material";
import { Box } from "@mui/system";
import { useIntl } from "react-intl";

import { ReactComponent as Button1 } from "./button1.svg";
import { ReactComponent as Button2 } from "./button2.svg";
import { ReactComponent as Button3 } from "./button3.svg";
import { ReactComponent as Button4 } from "./button4.svg";
import TimeTextField, { parseValue as parseTimeValue } from "../../TimeTextField";
import { DisplayZoneValue, Partition } from "../types";
import useEditedStyles from "../../../../../hooks/useEditedStyles";
import ZoneEvent from "../../zoneevent/ZoneEvent";
import { onNumberInputClick } from "../../../../../utils/utils";
interface ButtonSettingsValue {
  function: string;
  index?: number;
  state?: number;
  time?: number;
  partition?: string;
  press?: string;
  event?: string;
  zone?: string;
}

interface ButtonSettingsProps {
  value: ButtonSettingsValue;
  saved: ButtonSettingsValue | null;
  nr: number;
  partitions: Partition[] | null;
  zones: DisplayZoneValue[] | null;
  onChange: (nr: number, value: ButtonSettingsValue) => void;
  disabled?: boolean;
}

enum ButtonInputs {
  INDEX = 0b1,
  STATE = 0b10,
  TIME = 0b100,
  PARTITION = 0b1000,
  PRESS = 0b10000,
  EVENT = 0b100000,
  ZONE = 0b1000000,
}

const ButtonSettings = ({ value, saved, nr, partitions, zones, disabled, onChange }: ButtonSettingsProps) => {
  const [func, setFunc] = useState("NULL");
  const [selectedFunction, setSelectedFunction] = useState<{ value: string; label: string; inputs: number } | null>(null);
  const [portIndex, setPortIndex] = useState("");
  const [state, setState] = useState(false);
  const [time, setTime] = useState("");
  const [partition, setPartition] = useState("");
  const [press, setPress] = useState("N");
  const [events, setEvents] = useState({ alarm: value?.event || "" });
  const [zone, setZone] = useState("");

  const { formatMessage } = useIntl();
  const theme = useTheme();
  const editedStyle = useEditedStyles();

  const functions = useMemo(
    () => [
      { value: "NULL", label: formatMessage({ defaultMessage: "Unset", description: "Remote button" }), inputs: 0b0 },
      // {
      //   value: "AON",
      //   label: formatMessage({ defaultMessage: "Alarm On" }),
      //   inputs: ButtonInputs.INDEX | ButtonInputs.STATE | ButtonInputs.TIME,
      // },
      // {
      //   value: "AOFF",
      //   label: formatMessage({ defaultMessage: "Alarm Off" }),
      //   inputs: (ButtonInputs.INDEX & ButtonInputs.STATE) | ButtonInputs.TIME,
      // },
      // {
      //   value: "ATOG",
      //   label: formatMessage({ defaultMessage: "Alarm Toggle" }),
      //   inputs: ButtonInputs.INDEX | ButtonInputs.STATE | ButtonInputs.TIME,
      // },
      { value: "SAON", label: formatMessage({ defaultMessage: "Arm", description: "remote button" }), inputs: ButtonInputs.PARTITION },
      { value: "SAOFF", label: formatMessage({ defaultMessage: "Disarm", description: "remote button" }), inputs: ButtonInputs.PARTITION },
      // {
      //   value: "OON",
      //   label: formatMessage({ defaultMessage: "Output On" }),
      //   inputs: ButtonInputs.INDEX | ButtonInputs.STATE | ButtonInputs.TIME,
      // },
      // {
      //   value: "OOFF",
      //   label: formatMessage({ defaultMessage: "Output Off" }),
      //   inputs: ButtonInputs.INDEX | ButtonInputs.STATE | ButtonInputs.TIME,
      // },
      // {
      //   value: "OTOG",
      //   label: formatMessage({ defaultMessage: "Output Toggle" }),
      //   inputs: ButtonInputs.INDEX | ButtonInputs.STATE | ButtonInputs.TIME,
      // },
      { value: "SOS", label: formatMessage({ defaultMessage: "Panic" }), inputs: ButtonInputs.EVENT | ButtonInputs.PRESS },
      // { value: "EVENT", label: formatMessage({ defaultMessage: "Event" }), inputs: ButtonInputs.EVENT | ButtonInputs.ZONE },
      // {
      //   value: "ALL",
      //   label: "mind - teszt",
      //   inputs:
      //     ButtonInputs.EVENT | ButtonInputs.INDEX | ButtonInputs.PARTITION | ButtonInputs.PRESS | ButtonInputs.STATE | ButtonInputs.TIME | ButtonInputs.ZONE,
      // },
    ],
    [formatMessage]
  );

  const pressOptions = useMemo(
    () => [
      { value: "N", label: formatMessage({ defaultMessage: "Normal", description: "button press: one short press" }) },
      { value: "D", label: formatMessage({ defaultMessage: "Double", description: "button press" }) },
      { value: "L", label: formatMessage({ defaultMessage: "Long", description: "button press" }) },
    ],
    [formatMessage]
  );

  const handleFuncChange = useCallback(
    (func: string) => {
      setFunc(func);
      setSelectedFunction(functions.find((f) => f.value === func) || null);
      value.function = func;

      onChange(nr, value);
    },
    [onChange, functions, value, nr]
  );

  const handlePartitionChange = useCallback(
    (part: string) => {
      setPartition(part);
      value.partition = part;

      onChange(nr, value);
    },
    [onChange, value, nr]
  );

  const handlePressChange = useCallback(
    (press: string) => {
      setPress(press);
      value.press = press;

      onChange(nr, value);
    },
    [onChange, value, nr]
  );

  const handleZoneChange = useCallback(
    (zone: string) => {
      setZone(zone);
      if (zone) {
        value.zone = zone;
      } else {
        delete value.zone;
      }
      onChange(nr, value);
    },
    [onChange, value, nr]
  );

  useEffect(() => {
    setFunc(value.function);
    setSelectedFunction(functions.find((f) => f.value === value.function) || null);
    setPortIndex(value.index !== undefined ? `${value.index}` : "");
    setState(value.state !== undefined && value.state > 0);
    setTime(value.time !== undefined ? `${value.time}` : "");
    setPartition(value.partition || "");
    setEvents({ alarm: value?.event || "" });
    setZone(value.zone || "");
    setPress(value.press || "N");
  }, [value, functions]);

  useEffect(() => {
    if (!value || !partitions) {
      return;
    }
    setPartition((old) => {
      if (old !== "" && old !== "ALL" && !partitions.find((p) => p.uuid === old)) {
        value.partition = undefined;
        onChange(nr, value);
        return "";
      }
      return old;
    });
  }, [partitions, value, nr, onChange]);

  useEffect(() => {
    if (!value || !zones) {
      return;
    }
    setZone((old) => {
      if (old !== "" && !zones.find((z) => z.uuid === old)) {
        value.zone = undefined;
        onChange(nr, value);
        return "";
      }
      return old;
    });
  }, [zones, value, nr, onChange]);

  const icon = useMemo(() => {
    switch (nr) {
      case 1:
        return <Button1 height={30} width={30} color={theme.palette.text.primary} />;
      case 2:
        return <Button2 height={30} width={30} color={theme.palette.text.primary} />;
      case 3:
        return <Button3 height={30} width={30} color={theme.palette.text.primary} />;
      case 4:
        return <Button4 height={30} width={30} color={theme.palette.text.primary} />;
    }
  }, [nr, theme]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        marginY: 0.5,
      }}
      className={func === "NULL" ? "disabledrow" : ""}
    >
      <Grid item container spacing={1} alignItems="center" flexWrap="wrap">
        <Grid item sx={{ marginTop: 1, marginLeft: 1 }}>
          {icon}
        </Grid>
        <Grid item xs={12} sm="auto">
          <FormControl
            variant="filled"
            size="small"
            sx={{ margin: 0 }}
            className={saved?.function !== func ? editedStyle.input : ""}
            disabled={disabled}
            fullWidth
          >
            <InputLabel id="select-label">{formatMessage({ defaultMessage: "Function" })}</InputLabel>
            <Select labelId="select-label" value={func} onChange={(e) => handleFuncChange(e.target.value)}>
              {functions.map((option) => (
                <MenuItem key={`option-${option.value}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {selectedFunction?.inputs && (selectedFunction.inputs & ButtonInputs.INDEX) > 0 ? (
          <Grid item xs={12} sm="auto">
            <TextField
              label={formatMessage({ defaultMessage: "Index" })}
              variant="filled"
              value={portIndex}
              className={saved?.index !== parseInt(portIndex) ? editedStyle.input : ""}
              inputProps={{ min: 0, type: "number", onClick: onNumberInputClick }}
              onChange={(e) => setPortIndex(e.target.value)}
              onBlur={() => {
                value.index = value.index ? parseInt(portIndex, 10) : 0;
                onChange(nr, value);
              }}
              sx={{ margin: 0, minWidth: { sm: 80 }, width: { sm: 80 } }}
              size="small"
              autoComplete="off"
              disabled={disabled}
              fullWidth
            />
          </Grid>
        ) : null}
        {selectedFunction?.inputs && (selectedFunction.inputs & ButtonInputs.STATE) > 0 ? (
          <Grid item xs={12} sm="auto">
            <FormControl component="fieldset" sx={{ minWidth: 30, marginY: 0 }} disabled={disabled} fullWidth>
              <Box sx={{ display: "flex", flexDirection: "column" }} className={saved?.state !== (state ? 1 : 0) ? editedStyle.background : ""}>
                <FormLabel component="span" sx={{ ...theme.typography.caption }}>
                  {formatMessage({ defaultMessage: "State" })}
                </FormLabel>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={state}
                      onChange={() => {
                        const newState = !state;
                        setState(newState);
                        value.state = newState ? 1 : 0;
                        onChange(nr, value);
                      }}
                    />
                  }
                  label={state ? "1" : "0"}
                  labelPlacement="end"
                  sx={{ marginRight: 0, marginLeft: -0.3 }}
                />
              </Box>
            </FormControl>
          </Grid>
        ) : null}
        {selectedFunction?.inputs && (selectedFunction.inputs & ButtonInputs.TIME) > 0 ? (
          <Grid item xs={12} sm="auto">
            <TimeTextField
              label={formatMessage({ defaultMessage: "Time" })}
              variant="filled"
              value={time}
              className={saved?.time !== parseInt(time) ? editedStyle.input : ""}
              onChange={(e) => setTime(`${e.target.value}`)}
              onBlur={() => {
                value.time = parseTimeValue(time);
                onChange(nr, value);
              }}
              sx={{ margin: 0, minWidth: { sm: 160 }, width: { sm: 160 } }}
              size="small"
              disabled={disabled}
              fullWidth
            />
          </Grid>
        ) : null}
        {selectedFunction?.inputs && (selectedFunction.inputs & ButtonInputs.PARTITION) > 0 ? (
          <Grid item xs={12} sm="auto">
            <FormControl
              variant="filled"
              size="small"
              sx={{ margin: 0, minWidth: { sm: 220 }, width: { sm: 220 } }}
              className={saved?.partition !== partition || partitions?.find((p) => p.uuid === partition)?.deleted ? editedStyle.input : ""}
              disabled={disabled}
              fullWidth
            >
              <InputLabel id="part-select-label">{formatMessage({ defaultMessage: "Partition" })}</InputLabel>
              <Select labelId="part-select-label" value={partition} onChange={(e) => handlePartitionChange(e.target.value)}>
                <MenuItem key="part-all" value="ALL" sx={{ "ul > &": { color: "secondary.main" } }}>
                  {formatMessage({ defaultMessage: "All partitions" })}
                </MenuItem>
                {partitions
                  ?.filter((p) => p.uuid === partition || !p.deleted)
                  ?.map((part) => (
                    <MenuItem key={`part-${part.uuid}`} value={part.uuid} className={part.deleted ? editedStyle.withHover : ""} disabled={part.deleted}>
                      {[part.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |` : "", part.code, part.name]
                        .filter((i) => !!i)
                        .join(" ")}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        ) : null}
        {selectedFunction?.inputs && (selectedFunction.inputs & ButtonInputs.PRESS) > 0 ? (
          <Grid item xs={12} sm="auto">
            <FormControl
              variant="filled"
              size="small"
              sx={{ margin: 0, minWidth: { sm: 112 }, width: { sm: 112 } }}
              className={saved?.press !== press ? editedStyle.input : ""}
              disabled={disabled}
              fullWidth
            >
              <InputLabel id="press-select-label">{formatMessage({ defaultMessage: "Press" })}</InputLabel>
              <Select labelId="press-select-label" value={press} onChange={(e) => handlePressChange(e.target.value)}>
                {pressOptions.map((option) => (
                  <MenuItem key={`press-${option.value}`} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        ) : null}
        {selectedFunction?.inputs && (selectedFunction.inputs & ButtonInputs.EVENT) > 0 ? (
          <Grid item xs={12} sm="auto">
            {/* <TextField
              label={formatMessage({ defaultMessage: "Event" })}
              variant="filled"
              value={event}
              className={saved?.event !== event ? editedStyle.input : ""}
              inputProps={{ maxLength: 4, style: { textTransform: "uppercase" } }}
              onChange={(e) => hexInputCheck(e.target.value, setEvent)}
              onBlur={() => {
                const v = event ? event.padStart(4, "0") : event;
                value.event = v;
                // setEvent(v);
                onChange(nr, value);
              }}
              sx={{ margin: 0, minWidth: { sm: 220 }, width: { sm: 220 } }}
              size="small"
              autoComplete="off"
              disabled={disabled}
              fullWidth
            /> */}
            <ZoneEvent
              label={formatMessage({ defaultMessage: "Event" })}
              value={events}
              saved={saved ? { alarm: saved.event || "" } : undefined}
              onChange={({ alarm }) => {
                setEvents({ alarm });
                value.event = alarm;
                onChange(nr, value);
              }}
              disabled={disabled}
              xs="auto"
              noRecovery
            />
          </Grid>
        ) : null}
        {selectedFunction?.inputs && (selectedFunction.inputs & ButtonInputs.ZONE) > 0 ? (
          <Grid item xs={12} sm="auto">
            <FormControl
              variant="filled"
              size="small"
              sx={{ margin: 0, minWidth: { sm: 220 }, width: { sm: 220 } }}
              disabled={disabled}
              className={
                saved?.zone !== zone ||
                zones
                  ?.filter((z) => z.deleted)
                  .map((z) => z.code)
                  .includes(zone)
                  ? editedStyle.input
                  : ""
              }
              fullWidth
            >
              <InputLabel id="zone-select-label">{formatMessage({ defaultMessage: "Zone" })}</InputLabel>
              <Select labelId="zone-select-label" value={zone} onChange={(e) => handleZoneChange(e.target.value)}>
                <MenuItem key="zone-none" value="">
                  {formatMessage({ defaultMessage: "Without zone" })}
                </MenuItem>
                {zones?.map((zone) => (
                  <MenuItem key={`part-${zone.uuid}`} value={zone.uuid} className={zone.deleted ? editedStyle.withHover : ""} disabled={zone.disabled}>
                    {[zone.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, zone.code, zone.name].filter((i) => !!i).join(" ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

export default ButtonSettings;
